import { useEffect } from 'react';

// Kept as a function (not a module-level const) so tests can install/remove
// a navigator.mediaSession mock between cases.
function getMediaSession() {
  if (typeof navigator === 'undefined') return null;
  return navigator.mediaSession || null;
}

function artistFrom(currentAudio) {
  const p = currentAudio && currentAudio.participants;
  if (Array.isArray(p)) return p.join(', ');
  return p || '';
}

/**
 * Reflects app audio state into the OS media session so mobile browsers keep
 * playing with the screen locked and show real lock-screen controls.
 * Every interaction is feature-detected; on unsupported platforms it no-ops.
 */
export function useMediaSession({
  currentAudio,
  isPlaying,
  currentTime,
  duration,
  playbackRate,
  handlers,
}) {
  // Metadata — refresh whenever the track changes.
  useEffect(() => {
    const ms = getMediaSession();
    if (!ms || !currentAudio) return;
    if (typeof window !== 'undefined' && typeof window.MediaMetadata === 'function') {
      ms.metadata = new window.MediaMetadata({
        title: currentAudio.title || 'Episode',
        artist: artistFrom(currentAudio),
        album: 'Archive',
        artwork: [], // populated by the player-visual plan (cover-*.png)
      });
    }
  }, [currentAudio]);

  // playbackState — mirror transport so the OS shows the right play/pause icon.
  useEffect(() => {
    const ms = getMediaSession();
    if (!ms) return;
    ms.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  // Action handlers — route OS lock-screen/headset controls into app handlers.
  useEffect(() => {
    const ms = getMediaSession();
    if (!ms || typeof ms.setActionHandler !== 'function') return;
    const map = {
      play: handlers.play,
      pause: handlers.pause,
      previoustrack: handlers.previous,
      nexttrack: handlers.next,
      seekbackward: (d) => handlers.seekBy(-((d && d.seekOffset) || 10)),
      seekforward: (d) => handlers.seekBy((d && d.seekOffset) || 10),
      seekto: (d) => { if (d && d.seekTime != null) handlers.seekTo(d.seekTime); },
      stop: handlers.stop,
    };
    Object.keys(map).forEach((action) => {
      try { ms.setActionHandler(action, map[action] || null); } catch (e) { /* unsupported action */ }
    });
    return () => {
      Object.keys(map).forEach((action) => {
        try { ms.setActionHandler(action, null); } catch (e) { /* ignore */ }
      });
    };
  }, [handlers]);

  // Position state — keeps the lock-screen scrubber in sync.
  useEffect(() => {
    const ms = getMediaSession();
    if (!ms || typeof ms.setPositionState !== 'function') return;
    if (!duration || !isFinite(duration)) return;
    try {
      ms.setPositionState({
        duration,
        position: Math.min(currentTime || 0, duration),
        playbackRate: playbackRate || 1,
      });
    } catch (e) { /* invalid state — ignore */ }
  }, [currentTime, duration, playbackRate]);
}
